/**
 * Validation utilities for form inputs.
 * Centralized validation logic to ensure consistency across the application.
 */

/**
 * Email validation result.
 */
export interface ValidationResult {
  isValid: boolean;
  error: string | null;
}

/**
 * Validates an email address format.
 *
 * @param email - The email address to validate
 * @returns ValidationResult with isValid flag and optional error message
 *
 * @example
 * ```tsx
 * const { isValid, error } = validateEmail(email);
 * if (!isValid) {
 *   setError(error);
 *   return;
 * }
 * ```
 */
export function validateEmail(email: string): ValidationResult {
  if (!email.trim()) {
    return {
      isValid: false,
      error: 'Email address is required',
    };
  }

  // Standard email regex pattern
  // Matches: local@domain.tld
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return {
      isValid: false,
      error: 'Please enter a valid email address',
    };
  }

  return {
    isValid: true,
    error: null,
  };
}

/**
 * HTML5 pattern for phone number inputs — requires 10-20 characters of
 * digits, spaces, `+`, `(`, `)`, or `-`.
 *
 * Parens and the hyphen are escaped inside the character class because
 * modern browsers compile the `pattern` attribute in regex `v`-flag
 * (unicodeSets) mode, where unescaped `(`, `)`, `-` are a syntax error —
 * and per the HTML spec, a pattern that fails to compile is silently
 * treated as absent (validation just never runs, no console warning).
 */
export const PHONE_PATTERN = '[0-9+\\s\\(\\)\\-]{10,20}';
export const PHONE_PATTERN_TITLE = 'Please enter a valid phone number (at least 10 characters)';

export const PASSWORD_MIN_LENGTH = 8;

/**
 * Validates password strength: minimum length, at least one digit, and at
 * least one special (non-alphanumeric) character.
 *
 * @param password - The password to validate
 * @returns ValidationResult with isValid flag and optional error message
 */
export function validatePassword(password: string): ValidationResult {
  if (password.length < PASSWORD_MIN_LENGTH) {
    return {
      isValid: false,
      error: `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`,
    };
  }

  if (!/\d/.test(password)) {
    return {
      isValid: false,
      error: 'Password must include at least one number',
    };
  }

  if (!/[^A-Za-z0-9]/.test(password)) {
    return {
      isValid: false,
      error: 'Password must include at least one special character',
    };
  }

  return {
    isValid: true,
    error: null,
  };
}
